import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { StammkundeService } from '../../../Services/stammkunde.service';
import {
  Auftrag,
  AuftragAssignment,
  AuftragAssignmentCreate,
  EinsatzMaterial,
  PlannableUser,
  StammkundeWorker,
  TakedaEinsatzPlanLvPositionSave,
  TakedaTagesberichtLvPosition
} from '../../../Models/stammkunde.model';

/**
 * Eine geplante LV-Position im Dialog (SOLL). Die Planmenge ist optional – eine
 * Position darf auch nur vorgemerkt werden.
 */
interface PlanLvZeile {
  /** Gespeicherte Planzeile; erkennt freies Material beim Speichern wieder. */
  id?: number | null;
  /**
   * 'Lv' = LV-Position, 'Frei' = freies Material. Beide laufen durch denselben
   * Ablauf: Planen → geplante Materialien → Übernahme im Tagesbericht.
   */
  art: 'Lv' | 'Frei';
  /** Nur bei art 'Lv'. */
  lvPositionId: number | null;
  /** Bei freiem Material leer. */
  nummer: string;
  bezeichnung?: string | null;
  stichwort?: string | null;
  einheit?: string | null;
  planMenge: number | null;
}

/**
 * Eine bereits ERFASSTE Materialzeile dieses Einsatzes – readonly.
 *
 * Kommt aus der Materialprojektion und ist damit genau das, was auch die
 * Materialübersicht und die Abrechnung sehen. Bewusst nicht in `materials`
 * gemischt: diese Zeilen stammen aus anderen Entitäten und würden beim
 * Speichern sonst als Einsatzmaterial dupliziert.
 */
interface ErfassteMaterialZeile {
  nummer?: string | null;
  bezeichnung: string;
  menge: number;
  einheit?: string | null;
  herkunft: string;
}

export interface EinsatzFormData {
  auftrag: Auftrag;
  assignment?: AuftragAssignment;
  /** Login-Team-Mitglieder des Stammkunden (Verantwortlicher/Beteiligte). */
  users: PlannableUser[];
  /** Arbeiter ohne Login im Team des Stammkunden. */
  workers?: StammkundeWorker[];
  /** Vorbelegung aus dem Planungsboard (Drag & Drop auf einen Tag). */
  prefillDate?: string;
  /** Vorbelegung aus dem Planungsboard (Drag & Drop auf eine Personen-Zeile). */
  prefillUserId?: string;
  /** Vorbelegung aus dem Planungsboard (Drag & Drop auf eine Arbeiter-Zeile). */
  prefillWorkerId?: number;
}

/**
 * Dialog zum Planen eines Einsatzes (wer, wann, wo) für einen Stammkunden-Auftrag.
 * Optional wird direkt eine Outlook-/Kalender-Einladung (.ics) verschickt.
 */
@Component({
  selector: 'app-einsatz-form',
  templateUrl: './einsatz-form.component.html',
  styleUrls: ['./einsatz-form.component.css', '../stammkunden-theme.css']
})
export class EinsatzFormComponent implements OnInit, OnDestroy {
  isEdit: boolean;
  saving = false;

  assignedUserId = '';
  participantUserIds: string[] = [];
  workerIds: number[] = [];
  materials: EinsatzMaterial[] = [];

  /** Geplante LV-Positionen (SOLL, nur Takeda). */
  planLvPositionen: PlanLvZeile[] = [];

  /** Bereits erfasstes Material dieses Einsatzes – readonly, alle Quellen. */
  erfasstesMaterial: ErfassteMaterialZeile[] = [];
  erfasstesMaterialLoading = false;

  // LV-Suche für "+ LV-Position" – gleiche Mechanik wie in der Materialübersicht:
  // 300 ms Debounce, ab 2 Zeichen, laufende Anfragen werden verworfen.
  lvSearch = '';
  lvResults: TakedaTagesberichtLvPosition[] = [];
  lvLoading = false;
  lvPickerOffen = false;
  private readonly lvSearch$ = new Subject<string>();
  private readonly subs = new Subscription();

  /** Einheiten zur Auswahl bei freiem Material – dieselben wie bisher. */
  readonly einheiten = ['Stk', 'm', 'm²', 'm³', 'kg', 't', 'l', 'Pal', 'Pkg'];

  // Eingabemaske „Freies Material planen“ (nur Takeda) – Gegenstück zur LV-Suche.
  freiPickerOffen = false;
  freiBezeichnung = '';
  freiMenge: number | null = 1;
  freiEinheit = 'Stk';

  title = '';
  startDate = '';
  startTime = '07:00';
  endTime = '16:00';
  location = '';
  notes = '';
  // Standardmäßig KEINE Kalender-Einladung/E-Mail bei Stammkunden-Terminen (bewusst ankreuzbar).
  sendInvite = false;

  constructor(
    public dialogRef: MatDialogRef<EinsatzFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EinsatzFormData,
    private stammkundeService: StammkundeService,
    private snackBar: MatSnackBar
  ) {
    this.isEdit = !!data.assignment;
    const a = data.assignment;
    if (a) {
      this.assignedUserId = a.assignedUserId;
      this.participantUserIds = [...a.participantUserIds];
      this.workerIds = [...(a.workerIds ?? [])];
      this.materials = (a.materials ?? []).map(m => ({ ...m }));
      // LV-Zeilen ohne LV-Referenz (Stammposition gelöscht) fallen wie bisher weg;
      // freies Material hat nie eine LV-Referenz und bleibt.
      this.planLvPositionen = (a.planLvPositionen ?? [])
        .filter(p => p.art === 'Frei' || p.lvPositionId != null)
        .map(p => ({
          id: p.id,
          art: p.art === 'Frei' ? 'Frei' as const : 'Lv' as const,
          lvPositionId: p.art === 'Frei' ? null : p.lvPositionId!,
          nummer: p.nummer,
          bezeichnung: p.bezeichnung,
          stichwort: p.stichwort,
          einheit: p.einheit,
          planMenge: p.planMenge ?? null
        }));
      this.title = a.title;
      const start = new Date(a.start);
      const end = new Date(a.end);
      this.startDate = this.toDateInput(start);
      this.startTime = this.toTimeInput(start);
      this.endTime = this.toTimeInput(end);
      this.location = a.location ?? '';
      this.notes = a.notes ?? '';
      this.sendInvite = false;
    } else {
      this.title = data.auftrag.title;
      this.location = data.auftrag.location ?? '';
      const base = data.auftrag.startDate ? new Date(data.auftrag.startDate) : new Date();
      this.startDate = data.prefillDate ?? this.toDateInput(base);
      if (data.prefillUserId) {
        this.assignedUserId = data.prefillUserId;
      }
      if (data.prefillWorkerId != null) {
        this.workerIds = [data.prefillWorkerId];
      }
    }
  }

  ngOnInit(): void {
    if (!this.isTakedaAuftrag) {
      return;
    }

    // Live-Suche im aktiven Leistungsverzeichnis des Kunden.
    this.subs.add(this.lvSearch$
      .pipe(
        debounceTime(300),
        map(value => (value ?? '').trim()),
        distinctUntilChanged(),
        switchMap(term => {
          if (term.length < 2) {
            this.lvLoading = false;
            return of<TakedaTagesberichtLvPosition[]>([]);
          }
          this.lvLoading = true;
          return this.stammkundeService
            .searchTakedaProjektLvPositionen(this.data.auftrag.id, term)
            .pipe(catchError(err => {
              this.snackBar.open(
                err?.error?.message || 'Die LV-Positionen konnten nicht geladen werden.',
                'OK', { duration: 5000 });
              return of<TakedaTagesberichtLvPosition[]>([]);
            }));
        })
      )
      .subscribe(results => {
        this.lvResults = results ?? [];
        this.lvLoading = false;
      }));

    this.ladeErfasstesMaterial();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.lvSearch$.complete();
  }

  /** LV-Auswahl und Planmengen gibt es nur für Takeda-Aufträge. */
  get isTakedaAuftrag(): boolean {
    return !!this.data.auftrag?.isTakeda;
  }

  /**
   * Ist die Materialplanung gesperrt? Maßgeblich entscheidet das der Server
   * anhand des Tagesbericht-Status; hier wird nur gespiegelt.
   *
   * Nur ein ausdrückliches true sperrt – fehlt die Angabe, wird sie nicht als
   * Freigabe ausgelegt, sondern der Server weist ein Schreiben ohnehin ab.
   */
  get materialGesperrt(): boolean {
    return this.data.assignment?.materialplanungGesperrt === true;
  }

  /** Begründung für die Oberfläche – gleicher Wortlaut wie die Servermeldung. */
  readonly materialGesperrtHinweis =
    'Die Materialplanung ist gesperrt, da der Tagesbericht bereits abgeschlossen wurde.';

  /**
   * Freies Material, das es anzuzeigen gilt, wenn die Planung gesperrt ist –
   * dann gibt es keine Eingabemaske mehr, nur noch die Liste.
   */
  get materialsAnzeige(): EinsatzMaterial[] {
    // Takeda: freies Material wird geplant (planLvPositionen). Ältere direkt
    // erfasste Einsatzmaterialien zählen als erfasstes Material und stehen unter
    // „Bereits erfasst“ – hier würden sie doppelt erscheinen.
    if (this.isTakedaAuftrag) { return []; }
    return this.materials.filter(m => m.name?.trim());
  }

  /** Gibt es im gesperrten Zustand überhaupt etwas anzuzeigen? */
  get hatGeplantesMaterial(): boolean {
    return this.materialsAnzeige.length > 0 || this.planLvPositionen.length > 0;
  }

  get valid(): boolean {
    return !!this.assignedUserId && !!this.startDate && !!this.startTime && !!this.endTime
      && this.endTime > this.startTime;
  }

  // ── Material (Dokumentation, ohne Preise) ──

  addMaterial(): void {
    if (this.materialGesperrt) { return; }
    this.materials.push({ name: '', quantity: 1, unit: 'Stk' });
  }

  removeMaterial(index: number): void {
    if (this.materialGesperrt) { return; }
    this.materials.splice(index, 1);
  }

  // ── Geplante LV-Positionen (SOLL) ──

  /** Takeda: „Freies Material“ öffnet die Planungsmaske statt direkt zu erfassen. */
  toggleFreiPicker(): void {
    if (this.materialGesperrt) { return; }
    this.freiPickerOffen = !this.freiPickerOffen;
    this.lvPickerOffen = false;
    this.freiBezeichnung = '';
    this.freiMenge = 1;
    this.freiEinheit = 'Stk';
  }

  get kannFreiPlanen(): boolean {
    return this.freiBezeichnung.trim().length > 0;
  }

  /** Freies Material planen – erscheint danach in derselben Liste wie LV-Positionen. */
  addPlanFreiesMaterial(): void {
    if (this.materialGesperrt || !this.kannFreiPlanen) { return; }

    this.planLvPositionen.push({
      id: null,
      art: 'Frei',
      lvPositionId: null,
      nummer: '',
      bezeichnung: this.freiBezeichnung.trim(),
      stichwort: null,
      einheit: this.freiEinheit || null,
      planMenge: this.planMengeWert(this.freiMenge)
    });

    this.freiPickerOffen = false;
    this.freiBezeichnung = '';
    this.freiMenge = 1;
    this.freiEinheit = 'Stk';
  }

  toggleLvPicker(): void {
    if (this.materialGesperrt) { return; }
    this.freiPickerOffen = false;
    this.lvPickerOffen = !this.lvPickerOffen;
    this.lvSearch = '';
    this.lvResults = [];
    this.lvLoading = false;
  }

  onLvSearch(value: string): void {
    this.lvSearch = value;
    this.lvSearch$.next(value);
  }

  /**
   * Übernimmt eine LV-Position in die Planung. Die Planmenge bleibt zunächst leer –
   * sie ist optional und wird daneben eingetragen.
   */
  addPlanLvPosition(position: TakedaTagesberichtLvPosition): void {
    if (this.materialGesperrt) { return; }

    // Eine LV-Position darf pro Einsatz nur einmal geplant werden, sonst wäre die
    // Planmenge mehrdeutig (der Unique-Index im Backend sieht das genauso).
    if (this.planLvPositionen.some(p => p.art === 'Lv' && p.lvPositionId === position.id)) {
      this.snackBar.open('Diese LV-Position ist bereits geplant.', 'OK', { duration: 4000 });
      return;
    }

    this.planLvPositionen.push({
      id: null,
      art: 'Lv',
      lvPositionId: position.id,
      nummer: position.nummer,
      bezeichnung: position.bezeichnung,
      stichwort: position.stichwort,
      einheit: position.einheit,
      planMenge: null
    });

    this.lvPickerOffen = false;
    this.lvSearch = '';
    this.lvResults = [];
  }

  removePlanLvPosition(index: number): void {
    if (this.materialGesperrt) { return; }
    this.planLvPositionen.splice(index, 1);
  }

  planLvLabel(zeile: PlanLvZeile): string {
    return zeile.bezeichnung || zeile.stichwort || zeile.nummer;
  }

  /** Einheit eines freien Materials, die nicht in der Liste steht, bleibt wählbar. */
  einheitenFuer(zeile: PlanLvZeile): string[] {
    const e = zeile.einheit?.trim();
    return e && !this.einheiten.includes(e) ? [...this.einheiten, e] : this.einheiten;
  }

  // ── Bereits erfasstes Material (readonly) ──

  /**
   * Lädt alles, was diesem Einsatz schon zugeordnet ist – egal ob über einen
   * Tagesbericht, eine Bauleiter-Korrektur oder als Altbestand erfasst.
   *
   * Quelle ist die Materialprojektion, also genau der Bestand, den auch die
   * Materialübersicht und die Abrechnung sehen. Die Zeilen bleiben readonly und
   * getrennt von `materials`: sie stammen aus anderen Entitäten und würden beim
   * Speichern sonst als Einsatzmaterial dupliziert.
   */
  private ladeErfasstesMaterial(): void {
    const assignmentId = this.data.assignment?.id;
    if (!assignmentId) {
      return;
    }

    this.erfasstesMaterialLoading = true;

    this.subs.add(this.stammkundeService
      .getTakedaProjektDaten(this.data.auftrag.id)
      .pipe(catchError(() => of(null)))
      .subscribe(daten => {
        this.erfasstesMaterialLoading = false;
        if (!daten) {
          // Kein Zugriff oder kein Takeda-Projekt: der Block bleibt einfach leer,
          // der Dialog ist davon unabhängig bedienbar.
          return;
        }

        const zeilen: ErfassteMaterialZeile[] = [];

        for (const einsatz of daten.materialNachEinsatz ?? []) {
          if (einsatz.assignmentId !== assignmentId) {
            continue;
          }

          for (const bereich of einsatz.arbeitsbereiche ?? []) {
            for (const lv of bereich.lvPositionen ?? []) {
              zeilen.push({
                nummer: lv.nummer,
                bezeichnung: lv.bezeichnung || lv.stichwort || lv.nummer,
                menge: lv.menge,
                einheit: lv.einheit,
                herkunft: 'LV-Position'
              });
            }

            for (const frei of bereich.freieMaterialien ?? []) {
              zeilen.push({
                bezeichnung: frei.bezeichnung,
                menge: frei.menge,
                einheit: frei.einheit,
                herkunft: frei.art === 'Legacy'
                  ? 'Einsatzmaterial'
                  : 'Tagesbericht / Freies Material'
              });
            }
          }
        }

        this.erfasstesMaterial = zeilen;
      }));
  }

  /** Zeit-Schnellwahl (Ganztag/Vormittag/Nachmittag). */
  setTimes(start: string, end: string): void {
    this.startTime = start;
    this.endTime = end;
  }

  isTimes(start: string, end: string): boolean {
    return this.startTime === start && this.endTime === end;
  }

  save(): void {
    if (!this.valid) return;
    this.saving = true;

    const payload: AuftragAssignmentCreate = {
      assignedUserId: this.assignedUserId,
      participantUserIds: this.participantUserIds,
      workerIds: this.workerIds,
      // Bei gesperrter Planung bleiben BEIDE Materialfelder undefined. Das heißt
      // für den Server "unverändert lassen" – die übrigen Einsatzdaten (Personen,
      // Zeiten, Ort, Notizen) lassen sich weiter speichern. Würden wir die Listen
      // trotzdem senden, lehnte der Server den ganzen Aufruf ab.
      //
      // Die Id bleibt erhalten, damit der Server die Zeile wiedererkennt statt sie
      // zu löschen und neu anzulegen. Nur so behält sie ihre Freigabe und bleibt
      // als Herkunft einer Takeda-Materialkorrektur referenzierbar.
      // Takeda: Einsatzmaterial wird nicht mehr direkt erfasst (das zählte sofort
      // als Verbrauch), sondern geplant. Bestehende Zeilen bleiben unverändert.
      materials: this.materialGesperrt || this.isTakedaAuftrag
        ? undefined
        : this.materials
            .filter(m => m.name.trim())
            .map(m => ({
              id: m.id ?? null,
              name: m.name.trim(),
              quantity: m.quantity || 0,
              unit: m.unit?.trim() || 'Stk'
            })),
      // Geplante LV-Positionen (SOLL) nur für Takeda senden. Bei allen anderen
      // Stammkunden bleibt das Feld undefined – der Server lässt die Planung dann
      // unverändert, genau wie bei `materials`.
      planLvPositionen: this.isTakedaAuftrag && !this.materialGesperrt
        ? this.planLvPositionen
            .filter(p => p.art === 'Lv' || !!p.bezeichnung?.trim())
            .map(p => (p.art === 'Frei'
              ? {
                  id: p.id ?? null,
                  art: 'Frei',
                  bezeichnung: p.bezeichnung!.trim(),
                  einheit: p.einheit?.trim() || null,
                  planMenge: this.planMengeWert(p.planMenge)
                }
              : {
                  art: 'Lv',
                  lvPositionId: p.lvPositionId,
                  // Leere Eingabe bleibt leer: eine Position darf ohne Planmenge
                  // vorgemerkt werden.
                  planMenge: this.planMengeWert(p.planMenge)
                }) as TakedaEinsatzPlanLvPositionSave)
        : undefined,
      title: this.title.trim() || undefined,
      start: `${this.startDate}T${this.startTime}:00`,
      end: `${this.startDate}T${this.endTime}:00`,
      location: this.location.trim() || undefined,
      notes: this.notes.trim() || undefined,
      sendInvite: this.sendInvite
    };

    const request$ = this.isEdit
      ? this.stammkundeService.updateAssignment(this.data.auftrag.id, this.data.assignment!.id, payload)
      : this.stammkundeService.createAssignment(this.data.auftrag.id, payload);

    request$.subscribe({
      next: result => {
        this.saving = false;
        this.dialogRef.close(result);
      },
      error: err => {
        this.saving = false;
        const message = (typeof err?.error === 'string' && err.error)
          || err?.error?.message
          || 'Fehler beim Speichern des Einsatzes.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  userLabel(user: PlannableUser): string {
    return user.userName || user.email || user.id;
  }

  private toDateInput(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Leere, nicht-numerische oder nicht-positive Eingaben gelten als "keine Planmenge". */
  private planMengeWert(wert: number | null): number | null {
    const zahl = Number(wert);
    return Number.isFinite(zahl) && zahl > 0 ? zahl : null;
  }

  private toTimeInput(date: Date): string {
    const h = `${date.getHours()}`.padStart(2, '0');
    const min = `${date.getMinutes()}`.padStart(2, '0');
    return `${h}:${min}`;
  }
}
